import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Nullable } from "primereact/ts-helpers";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";

import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import TextInput from "../../../../../Components/TextInput";
import FileInput from "../../../../../Components/FileInput";
import DatePicker from "../../../../../Components/DatePicker";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { goToTop } from "../../../../../Components/goToTop";

import { checkFileOthers, checkText } from "../../../../../Utils/Validator";
import { refinancingCreateAPI, refinancingDeleteAPI, refinancingGetDataApi, refinancingUpdateAPI } from "../../../../../Api/Project/DefaultServices/RefinancingApi";
import { Refinancings } from "../../../../../Controllers/Types/Project/DefaultService/RefinancingInterface";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import { Tooltip } from "primereact/tooltip";

const Refinancing: React.FC = () => {
    const location = useLocation();
    const today = new Date();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);
    const isShared: boolean = projectData?.sharedBy !== null;
    const canEdit: boolean = Boolean(projectData?.can_edit);

    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [statuses] = useState<string[]>(['Received', 'In Progress', 'Pending']);

    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    const [refinancingData, setRefinancingData] = useState<Refinancings[]>([]);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [selectedRefinancingData, setSelectedRefinancingData] = useState<Refinancings | null>(null);


    /* ****************************** Form variables **********************************/
    const [certificateTitle, setCertificateTitle] = useState<string>('');
    const [appliedDate, setAppliedDate] = useState<Nullable<Date>>(null);
    const [status, setStatus] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    /* Error message */
    const [certificateTitleError, setCertificateTitleError] = useState<string>('');
    const [appliedDateError, setAppliedDateError] = useState<string>('');

    const [statusError, setStatusError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    /* **************************************************************************** */

    const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const hasPermissionForAddRefinancing = useCanEdit('Refinancing', 'add_refinancing');
    const hasPermissionForEditRefinancing = useCanEdit('Refinancing', 'edit_refinancing');
    const hasPermissionForDeleteRefinancing = useCanEdit('Refinancing', 'delete_refinancing');

    const checkAccessPermission = useCallback(
        (permission: boolean): boolean => {
            return isShared ? canEdit : permission;
        },
        [isShared, canEdit]
    );

    useEffect(() => {
        refinancingDataGet();
    }, [serviceId]);

    /* ********************************* get data list ************************************************** */
    const refinancingDataGet = async () => {
        if (serviceId) {
            setDataLoading(true);
            const data = await refinancingGetDataApi(Number(serviceId));

            setDataLoading(false);
            setInitialDataLoading(false);
            if (data.status) {
                setRefinancingData(data.data || []);
            } else {
                setErrorMessage(`An error occurred while retrieving ${(projectData.feature_name).toLowerCase()} document data.`);
            }
        }
    }
    /* ********************************* get data list ************************************************** */


    /* ***************************** For refinancing ***************************** */
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        handleClearFormFields();
        handleClearFormErrors();
    }

    const handleClearFormFields = () => {
        setCertificateTitle('');
        setAppliedDate(null);
        setStatus('');
        setFile(null);
        setFileName('');
        setSelectedRefinancingData(null);
    }

    const handleClearFormErrors = () => {
        setCertificateTitleError('');
        setAppliedDateError('');
        setStatusError('');
        setFileError('');
    }

    const handleAddDetails = () => {
        setShowDetailModal(true);
        setDataState('Add');
    }

    useEffect(() => {
        checkText(certificateTitle, false, setCertificateTitleError);
    }, [certificateTitle]);

    useEffect(() => {
        checkText(formatDate(appliedDate || null), false, setAppliedDateError);
    }, [appliedDate]);

    useEffect(() => {
        checkText(status!, false, setStatusError);
    }, [status]);

    useEffect(() => {
        checkFileOthers(file!, false, setFileError);
    }, [file]);

    function checkCredentials(appliedDate: string): boolean {
        const isCertificateTitleValid = checkText(certificateTitle, true, setCertificateTitleError);
        const isAppliedDateValid = checkText(appliedDate, false, setAppliedDateError);
        const isStatusValid = checkText(status!, true, setStatusError);
        const isFileValid = checkFileOthers(file!, false, setFileError);

        return isCertificateTitleValid && isAppliedDateValid && isStatusValid && isFileValid;
    }

    /* ******************************************* Create ******************************************* */
    const handleCreateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        const appliedDateDateOnly = formatDate(appliedDate || null);
        if (checkCredentials(appliedDateDateOnly)) {
            setLoading(true);
            const data = await refinancingCreateAPI(
                Number(serviceId),
                certificateTitle,
                appliedDateDateOnly!,
                status!,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                refinancingDataGet();
                handleCloseDetailModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `${projectData.feature_name} details added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating refinancing details.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ******************************************* Create ******************************************* */


    const handleEditDetails = (refData: Refinancings) => {
        setShowDetailModal(true);
        setDataState('Edit');
        setSelectedRefinancingData(refData);

        setCertificateTitle(refData?.certificate_title);
        setAppliedDate(parseStringToDate(refData?.applied_date || ''));
        setStatus(refData?.status);
    }

    /* ******************************************* Update ******************************************* */
    useEffect(() => {
        if (selectedRefinancingData) {
            if (certificateTitle !== selectedRefinancingData?.certificate_title) {
                setUpdateHasChanges(true);
            } else if (formatDate(appliedDate!) !== selectedRefinancingData?.file_upload_date) {
                setUpdateHasChanges(true);
            } else if (status !== selectedRefinancingData?.status) {
                setUpdateHasChanges(true);
            } else if (file !== null) {
                setUpdateHasChanges(true);
            }
            else {
                setUpdateHasChanges(false);
            }
        }
    }, [selectedRefinancingData, certificateTitle, appliedDate, status, file])

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        const appliedDateDateOnly = formatDate(appliedDate || null);
        if (checkCredentials(appliedDateDateOnly)) {
            setLoading(true);

            const data = await refinancingUpdateAPI(
                Number(selectedRefinancingData?.id!),
                certificateTitle,
                appliedDateDateOnly!,
                status!,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                refinancingDataGet();
                handleCloseDetailModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `${projectData.feature_name} details updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating refinancing details.',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ******************************************* Update ******************************************* */


    /* ******************************************* Delete ******************************************* */
    const handleDeleteData = (refId: number): void => {
        confirmDialog({
            message: `Are you sure you want to delete this ${projectData.feature_name} data?`,
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(refId),
            dismissableMask: true,
            draggable: false,
            resizable: false
        });
    }

    const deleteData = async (refId: number) => {
        setdeleteLoadingId(refId);
        const data = await refinancingDeleteAPI(refId);

        setdeleteLoadingId(null);
        if (data.statusCode === 200) {
            refinancingDataGet();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `${projectData.feature_name} data deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting refinancing data.',
                    life: 3000
                });
            }
        }
    }
    /* ******************************************* Delete ******************************************* */


    const onStatusChange = (e: any) => {
        setStatus(e.value);
    };

    /* For showing statuses options in the dropdown list */
    const statusOptionTemplate = (option: string) => {
        let colorCode = '';
        if (option === 'Received') {
            colorCode = '#B9F6CA';
        } else if (option === 'In Progress') {
            colorCode = '#FFEFBC';
        } else if (option === 'Pending') {
            colorCode = '#EF9A9A';
        }
        return (
            <div className="d-flex align-items-center w-100 justify-content-between">
                <div>{option}</div>
                <div className="color_display" style={{ backgroundColor: colorCode }}></div>
            </div>
        );
    };

    const getColorCode = (status: string): string => {
        switch (status) {
            case 'Received':
                return '#B9F6CA';
            case 'In Progress':
                return '#FFEFBC';
            default:
                return '#EF9A9A';
        }
    };

    /* for file upload */
    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };


    /* Modal header */
    const detailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;{projectData.feature_name} details
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseDetailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const detailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseDetailModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateDetails : handleUpdateDetails}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );
    /* *************************************************************************** */


    /* ***************************** For table view ****************************** */
    const getRowClassName = (data: Refinancings, options: { [key: string]: any }) => {
        if (data.status === 'Received') {
            return 'table_data_row success';
        } else if (data.status === 'In Progress') {
            return 'table_data_row warning';
        } else if (data.status === 'Pending') {
            return 'table_data_row danger';
        }
    };

    /* Table structure */
    // const editDataBody = (rowData: Refinancings) => {
    //     const data = rowData;
    //     return (
    //         <button
    //             className="data_action_btn primary_table_btn p-ripple"
    //             onClick={() => handleEditDetails(data)}
    //         >
    //             <i className="bi bi-pencil-square"></i>
    //             <Ripple />
    //         </button>
    //     )
    // }

    // const deleteDataBody = (rowData: Refinancings) => {
    //     const dataId = rowData.id;

    //     return (
    //         <React.Fragment>
    //             {deleteLoadingId === dataId ? (
    //                 <TableActionLoader />
    //             ) : (
    //                 <button
    //                     className="data_action_btn danger_table_btn p-ripple"
    //                     onClick={() => handleDeleteData(dataId)}
    //                 >
    //                     <i className="bi bi-trash3"></i>
    //                     <Ripple />
    //                 </button>
    //             )}
    //         </React.Fragment>
    //     )
    // }

    const viewDocumentBody = (rowData: Refinancings) => {
        const filePath = rowData.path;
        return (
            <div className="tabel_btn_area justify-content-end justify-content-lg-center mt-1 mb-1">
                <Button
                    label="View"
                    className="tabel_btn view_btn"
                    disabled={!filePath}
                    onClick={() => handleViewDocument(filePath ?? '')}
                />
            </div>
        )
    };
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: string) => {
        setFileUrl(document);
        setShowFileViewModal(true);
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    // table action body

    const refinancingTableActionBody = (rowData: Refinancings) => {
        const data = rowData;
        const filePath = rowData.path;
        return (
            <React.Fragment>
                <Tooltip target=".data_action_btn" />

                <div className="tabel_btn_area custom mt-1 mb-1">
                    {checkAccessPermission(hasPermissionForEditRefinancing) && (
                        <button
                            className="data_action_btn with_bg primary_table_btn p-ripple"
                            onClick={() => handleEditDetails(data)}
                            data-pr-tooltip={`Edit`}
                            data-pr-position="top"
                        >
                            <i className="bi bi-pencil-square"></i>
                            <Ripple />
                        </button>
                    )}

                    {checkAccessPermission(hasPermissionForDeleteRefinancing) && (
                        <button
                            className="data_action_btn with_bg danger_table_btn p-ripple"
                            onClick={() => handleDeleteData(data.id)}
                            data-pr-tooltip={`Delete`}
                            data-pr-position="top"
                        >
                            <i className="bi bi-trash3"></i>
                            <Ripple />
                        </button>
                    )}



                    <button
                        className="data_action_btn with_bg primary_table_btn p-ripple"
                        onClick={() => handleViewDocument(filePath ?? '')}
                        data-pr-tooltip={`View`}
                        disabled={!filePath}
                        data-pr-position="top"

                    >
                        <i className="bi bi-eye"></i>
                        <Ripple />
                    </button>

                </div>
            </React.Fragment>
        );
    };



    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="status_lable_container">
                                {refinancingData?.length > 0 && (
                                    <div className="status_lable_area">
                                        {statuses?.map((status, index) => {
                                            const colorCode = getColorCode(status);
                                            return (
                                                <div key={index} className="status_lable">
                                                    <span style={{ backgroundColor: colorCode }}></span>
                                                    <h6>{status}</h6>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {checkAccessPermission(hasPermissionForAddRefinancing) &&
                                <div className="page_action_area">
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddDetails}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add detail</span>
                                        <Ripple />
                                    </button>
                                </div>
                            }
                        </div>

                        <div className="page_main_content_section">
                            {refinancingData?.length > 0 ? (
                                <DataTable
                                    value={refinancingData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    rowClassName={getRowClassName}
                                    loading={dataLoading}
                                    scrollable
                                    dataKey="id"
                                >
                                    <Column
                                        header="Document name"
                                        headerClassName="text_purple"
                                        body={(rowData, { rowIndex }) => (
                                            <span className="text-capitalize">{rowIndex + 1}.&nbsp;&nbsp;&nbsp;
                                                {rowData.certificate_title ? rowData.certificate_title : '--'}
                                            </span>
                                        )}
                                        style={{ width: '40%' }}
                                    ></Column>

                                    <Column
                                        header="Date"
                                        headerClassName="text_purple"
                                        alignHeader={'center'}
                                        body={(rowData) => (
                                            <span className="text_no_wrap flex_center">
                                                {rowData.applied_date ? rowData.applied_date : '--'}
                                            </span>
                                        )}
                                        style={{ width: '20%' }}
                                    ></Column>


                                    <Column
                                        alignHeader="center"
                                        body={refinancingTableActionBody}
                                        frozen
                                        alignFrozen="right"
                                    ></Column>

                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${(projectData.feature_name).toLowerCase()} document added. ${checkAccessPermission(hasPermissionForAddRefinancing) ? 'To add the document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Refinancing detail modal */}
            <Dialog
                visible={showDetailModal}
                header={detailModalHeader}
                footer={detailModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDetailModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-4 pt-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            {/* Certificate title */}
                            <div className="col-12">
                                <TextInput
                                    id="certificateTitle"
                                    label="Certificate title"
                                    labelHtmlFor="certificateTitle"
                                    required={true}
                                    value={certificateTitle}
                                    placeholder="Certificate title"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCertificateTitle(e.target.value)}
                                    error={certificateTitleError}
                                />
                            </div>

                            {/* Applied date */}
                            <div className="col-12">
                                <DatePicker
                                    id="appliedDate"
                                    label="Applied date"
                                    labelHtmlFor="appliedDate"
                                    required={false}
                                    value={appliedDate}
                                    minDate={today}
                                    placeholder="dd/mm/yyyy"
                                    onChange={setAppliedDate}
                                    error={appliedDateError}
                                    showClear
                                    onClear={() => setAppliedDate(null)}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="status" className="custom_form_label is_required">Status</label>
                                    <Dropdown
                                        id="status"
                                        value={status}
                                        onChange={onStatusChange}
                                        options={statuses}
                                        itemTemplate={statusOptionTemplate}
                                        panelClassName="status_option_panel"
                                        placeholder="Select status"
                                        className="form_dropdown w-100"
                                        showClear
                                    />

                                    {statusError &&
                                        <small className="form_error_msg">{statusError}</small>
                                    }
                                </div>
                            </div>

                            {/* File */}
                            <div className="col-12">
                                <FileInput
                                    id="file"
                                    label="Upload file"
                                    labelHtmlFor="file"
                                    required={false}
                                    value={fileName}
                                    onUpload={handleFileUpload}
                                    onClear={handleClearFile}
                                    toast={toast}
                                    error={fileError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog >


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
            />
        </React.Fragment>
    )
}

export default Refinancing;